import { FC, ReactNode, useMemo } from "react";
import { noop } from "@common/helpers/function";
import { ConnectAccountContext } from "@components/connectAccount/ConnectAccount.context";
import { orgConnectContext } from "./OrgConnect.context";

/**
 * Marks everything inside as an organization-level connect and neutralizes the
 * workspace connect-account state for it.
 *
 * The post-connect state drives modals that only make sense inside a client:
 * the "Loading accounts" modal (which has a static backdrop and no close
 * button) and the entity classification modals. Connect components write to
 * that state directly as well as through their mutations, so suppressing only
 * some writes would leave `isConnecting` stuck at true and the undismissable
 * loader on screen. Neutralizing the whole context keeps it impossible for any
 * write path — current or future — to strand the firm on that modal.
 */
export const OrgConnectProvider: FC<{
    organizationId: string;
    children: ReactNode;
}> = ({ organizationId, children }) => {
    const value = useMemo(() => ({ organizationId }), [organizationId]);
    const inertConnectAccount = useMemo(
        () => ({
            state: { isConnecting: false } as const,
            updateConnectAccountState: noop,
            updateFinancialAccount: noop,
        }),
        [],
    );

    return (
        <orgConnectContext.Provider value={value}>
            <ConnectAccountContext.Provider value={inertConnectAccount}>
                {children}
            </ConnectAccountContext.Provider>
        </orgConnectContext.Provider>
    );
};
